import React, { useState, useEffect, useRef } from "react";

import classNames from "classnames";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useParams } from "react-router-dom";
import { FixedSizeList as List } from "react-window";

import crosswalkMappingsAPI from "apis/crosswalkMappings";
import referenceSourceValuesApi from "apis/reference_source_values";
import referenceSourcesExploreApi from "apis/referenceSourcesExplore";
import {
  PageLoader,
  SearchBar,
  SubHeader,
  Typography,
} from "clearsense-ui/components";
import useDebounce from "hooks/useDebounce";
import { useOnClickOutside } from "hooks/useOnClickOutside";

import ValueAccordion from "./ValueAccordion";
import ValueCard from "./ValueCard";

const CrosswalkMapping = () => {
  const { crosswalkId, referenceSourceId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isActionPending, setIsActionPending] = useState(false);
  const [crosswalk, setCrosswalk] = useState({});
  const [firstSearchTerm, setFirstSearchTerm] = useState("");
  const [secondSearchTerm, setSecondSearchTerm] = useState("");
  const [highlightedValue, setHighlightedValue] = useState({});
  const [firstReferenceValues, setFirstReferenceValues] = useState([]);
  const [secondReferenceValues, setSecondReferenceValues] = useState([]);
  const [isExpanded, setIsExpanded] = useState({});
  const sourceValuesDivRef = useRef(null);
  const debouncedSearchForFirstRefValues = useDebounce(firstSearchTerm, 300);
  const debouncedSearchForSecondRefValues = useDebounce(secondSearchTerm, 300);

  const handleValueSelection = selectedValue => {
    if (selectedValue.isMapped) {
      const { id } = secondReferenceValues.find(value =>
        value.values.includes(selectedValue.id)
      );
      if (isExpanded[id]) {
        if (highlightedValue.id !== selectedValue.id) {
          setHighlightedValue(selectedValue);
        }
      } else {
        toggleVisibility(id);
        setHighlightedValue(selectedValue);
      }
    } else {
      setHighlightedValue({});
    }
  };

  const toggleVisibility = selectedValueId => {
    setIsExpanded(isExpanded =>
      Object.fromEntries(
        Object.keys(isExpanded).map(id =>
          selectedValueId === id ? [id, !isExpanded[id]] : [id, false]
        )
      )
    );

    setHighlightedValue({});

    if (!isExpanded[selectedValueId]) {
      const mappedReferenceSourceValue = secondReferenceValues.find(
        value => value.id === selectedValueId
      );

      const newReferenceValuesArray = firstReferenceValues.map(value =>
        mappedReferenceSourceValue.values?.includes(value.id)
          ? { ...value, isHighLighted: true }
          : { ...value, isHighLighted: false }
      );
      setFirstReferenceValues(newReferenceValuesArray);
    } else {
      setFirstReferenceValues(() =>
        firstReferenceValues.map(value => ({
          ...value,
          isHighLighted: false,
        }))
      );
    }
  };

  const onMapping = async (sourceValue, targetValue) => {
    try {
      setIsActionPending(true);
      await crosswalkMappingsAPI.create(crosswalkId, {
        value_from_id: sourceValue.id,
        value_to_id: targetValue.id,
      });
      setIsActionPending(false);
      const mappedValues = secondReferenceValues.map(value =>
        value.id === targetValue.id
          ? { ...value, values: [...value.values, sourceValue.id] }
          : value
      );
      setHighlightedValue(sourceValue);
      setSecondReferenceValues(mappedValues);
      setFirstReferenceValues(firstReferenceValues =>
        firstReferenceValues.map(value =>
          value.id === sourceValue.id
            ? { ...value, isMapped: true, isHighLighted: true }
            : value
        )
      );
    } catch (error) {
      logger.log(error);
    }
  };

  const handleInitialMapping = async () => {
    try {
      setIsLoading(true);
      const { data } = await referenceSourcesExploreApi.showCrosswalk(
        referenceSourceId,
        crosswalkId
      );
      setCrosswalk(data);
      const mappingData = await Promise.all([
        referenceSourceValuesApi.index(
          data?.reference_source_from?.id,
          "",
          1,
          100
        ),
        await referenceSourceValuesApi.index(
          data?.reference_source_to?.id,
          "",
          1,
          100
        ),
        crosswalkMappingsAPI.fetch(crosswalkId),
      ]);

      setFirstReferenceValues(
        mappingData?.[0]?.data?.reference_source_values?.map(value => ({
          ...value,
          isMapped: mappingData?.[2]?.data?.crosswalk_mappings?.find(
            mapping => mapping?.value_from?.id === value?.id
          ),
          isHighLighted: false,
        }))
      );
      setSecondReferenceValues(
        mappingData?.[1]?.data?.reference_source_values?.map(value => ({
          ...value,
          values: mappingData?.[2]?.data?.crosswalk_mappings?.reduce(
            (acc, mapping) => {
              if (mapping?.value_to?.id === value?.id) {
                return [...acc, mapping?.value_from?.id];
              }

              return acc;
            },
            []
          ),
        }))
      );
      setIsExpanded(
        Object.fromEntries(
          mappingData?.[1]?.data?.reference_source_values?.map(value => [
            value.id,
            false,
          ])
        )
      );
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getReferenceSourceName = (direction, id) => {
    if (direction === "current") {
      if (crosswalk?.reference_source_from?.id === id) {
        return crosswalk?.reference_source_from?.name;
      }

      return crosswalk?.reference_source_to?.name;
    }

    return crosswalk?.reference_source_from?.id === id
      ? `Crosswalk Out with '${crosswalk?.reference_source_to?.name}'`
      : `Crosswalk In with '${crosswalk?.reference_source_from?.name}'`;
  };

  const disassociateValue = async (event, firstRefValueId) => {
    try {
      setIsActionPending(true);
      event.stopPropagation();
      const secondRefValue = secondReferenceValues.find(value =>
        value.values.includes(firstRefValueId)
      );
      await crosswalkMappingsAPI.destroy(
        crosswalkId,
        firstRefValueId,
        secondRefValue.id
      );
      const mappedValues = secondReferenceValues.map(value =>
        value.id === secondRefValue.id
          ? {
              ...value,
              values: value.values.filter(
                valueId => valueId !== firstRefValueId
              ),
            }
          : value
      );
      setSecondReferenceValues(mappedValues);
      setFirstReferenceValues(firstReferenceValues =>
        firstReferenceValues.map(value =>
          value.id === firstRefValueId
            ? { ...value, isMapped: false, isHighLighted: false }
            : value
        )
      );
    } catch (error) {
      logger.error(error);
    } finally {
      setIsActionPending(false);
    }
  };

  const CardRow = ({ index, style, data }) => (
    <ValueCard
      style={style}
      key={index}
      value={data?.[index]}
      targetValues={secondReferenceValues}
      disassociateValue={disassociateValue}
      highlightedValue={highlightedValue}
      handleValueSelection={handleValueSelection}
    />
  );

  useEffect(() => {
    handleInitialMapping();
  }, []);

  useOnClickOutside(sourceValuesDivRef, () => setHighlightedValue({}));

  return isLoading ? (
    <PageLoader />
  ) : (
    <div
      className={classNames("cs-ui-container cs-ui-container__table", {
        "pointer-events-none select-none": isActionPending,
      })}
    >
      {isActionPending && (
        <div className="absolute top-0 bottom-0 left-0 right-0 bg-white dark:bg-gray-700 opacity-60 select-none pointer-events-none">
          <PageLoader />
        </div>
      )}
      <SubHeader
        className="mt-8"
        breadcrumbs={[
          {
            label: "Reference Sources",
            link: "/management/reference-data/reference-sources",
          },
          {
            label: getReferenceSourceName("current", referenceSourceId),
            link: `/management/reference-data/reference-sources/${referenceSourceId}`,
          },
          {
            label: "Crosswalk",
            link: `/management/reference-data/reference-sources/${referenceSourceId}/crosswalks`,
          },
          {
            label: getReferenceSourceName("other", referenceSourceId),
            link: `#`,
          },
        ]}
      />

      <div className="h-[58vh]">
        <div className="flex w-full justify-between mt-6 space-x-16">
          <div className="flex w-[max(20rem,50%)] flex-col space-y-2">
            <Typography
              style="h3"
              type="semibold"
              data-cy="from-reference-source-title"
            >
              From: {crosswalk?.reference_source_from?.name}
            </Typography>
            <div className="pr-4">
              <SearchBar
                value={firstSearchTerm}
                onChange={e => setFirstSearchTerm(e.target.value)}
                data-cy="from-values-search-field"
              />
            </div>
          </div>
          <div className="flex w-3/4 flex-col space-y-2">
            <Typography
              style="h3"
              type="semibold"
              data-cy="to-reference-source-title"
            >
              To: {crosswalk?.reference_source_to?.name}
            </Typography>
            <div className="pr-4">
              <SearchBar
                value={secondSearchTerm}
                onChange={e => setSecondSearchTerm(e.target.value)}
                data-cy="to-values-search-field"
              />
            </div>
          </div>
        </div>
        <DndProvider backend={HTML5Backend}>
          <div className="flex w-full justify-between mt-6 space-x-16 h-full">
            <div
              className="flex flex-col space-y-2 overflow-auto pr-4 w-[max(20rem,50%)] h-full"
              ref={sourceValuesDivRef}
            >
              <List
                height={1000}
                width="100%"
                itemCount={
                  firstReferenceValues?.filter(value =>
                    value?.name
                      ?.toLowerCase()
                      ?.includes(debouncedSearchForFirstRefValues)
                  )?.length
                }
                itemSize={75}
                itemData={firstReferenceValues?.filter(value =>
                  value?.name
                    ?.toLowerCase()
                    ?.includes(debouncedSearchForFirstRefValues)
                )}
                data-cy="reference-source-draggable-card-container"
              >
                {CardRow}
              </List>
            </div>
            <div
              className="flex flex-col space-y-2 overflow-auto pr-4 w-3/4"
              data-cy="value-card-accordion-container"
            >
              {secondReferenceValues.map(
                value =>
                  value?.name
                    ?.toLowerCase()
                    ?.includes(
                      debouncedSearchForSecondRefValues.trim().toLowerCase()
                    ) && (
                    <ValueAccordion
                      onMapping={onMapping}
                      targetValues={secondReferenceValues}
                      secondRefValue={value}
                      firstRefValues={firstReferenceValues}
                      key={value.id}
                      disassociateValue={disassociateValue}
                      isExpanded={isExpanded}
                      selectedValueId={highlightedValue.id}
                      toggleVisibility={toggleVisibility}
                    />
                  )
              )}
            </div>
          </div>
        </DndProvider>
      </div>
    </div>
  );
};

export default CrosswalkMapping;
